import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebaseConfig';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  updateProfile,
  updateEmail as firebaseUpdateEmail,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail
} from 'firebase/auth';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = async (email, pass) => {
    await signInWithEmailAndPassword(auth, email, pass);
  };

  const register = async (email, pass, name) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
    await updateProfile(userCredential.user, { displayName: name });
    // Force user update to reflect display name immediately
    setUser({ ...userCredential.user, displayName: name });
    
    // Send welcome email (non-blocking)
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${API_URL}/api/email/welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name }),
      }).catch(() => {}); // Ignore errors, don't block signup
    } catch (e) {
      // Silent fail - welcome email is not critical
    }
  };

  const logout = () => {
    return firebaseSignOut(auth);
  };

  // Re-authenticate user (required for sensitive operations)
  const reauthenticate = async (currentPassword) => {
    if (!user?.email) throw new Error('No user logged in');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
  };

  // Update display name
  const updateDisplayName = async (newName) => {
    if (!auth.currentUser) throw new Error('No user logged in');
    await updateProfile(auth.currentUser, { displayName: newName });
    // Force refresh user state
    setUser({ ...auth.currentUser, displayName: newName });
  };

  // Update email (requires re-authentication)
  const updateEmail = async (newEmail, currentPassword) => {
    if (!auth.currentUser) throw new Error('No user logged in');
    // Re-authenticate first
    await reauthenticate(currentPassword);
    await firebaseUpdateEmail(auth.currentUser, newEmail);
    // Force refresh user state
    setUser({ ...auth.currentUser, email: newEmail });
  };

  // Update password (requires re-authentication)
  const updateUserPassword = async (newPassword, currentPassword) => {
    if (!auth.currentUser) throw new Error('No user logged in');
    // Re-authenticate first
    await reauthenticate(currentPassword);
    await firebaseUpdatePassword(auth.currentUser, newPassword);
  };

  // Send password reset email
  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout,
      updateDisplayName,
      updateEmail,
      updateUserPassword,
      resetPassword
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
